import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth/AuthContext";

function HeaderIconTooltip({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Tooltip
      arrow
      enterDelay={200}
      leaveDelay={0}
      placement="bottom"
      title={title}
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: "#1e2936",
            color: "#f4f6f8",
            px: 1.25,
            py: 0.85,
            borderRadius: 1.5,
            boxShadow: "0 8px 24px rgba(15, 23, 32, 0.28)",
            fontWeight: 700,
            fontSize: "0.8125rem",
          },
        },
        arrow: {
          sx: { color: "#1e2936" },
        },
      }}
    >
      <span style={{ display: "inline-flex" }}>{children}</span>
    </Tooltip>
  );
}

const iconButtonSx = {
  color: "text.secondary",
  opacity: 0.45,
  "&:hover": {
    opacity: 0.9,
    bgcolor: "transparent",
  },
} as const;

export function StaffAppHeader({
  title,
  leading,
  trailing,
  actions,
  active = "board",
}: {
  title: string;
  /** e.g. open-request count badge */
  leading?: ReactNode;
  /** e.g. live status chip */
  trailing?: ReactNode;
  /** Page-specific controls (TV mode, filters, refresh…) */
  actions?: ReactNode;
  active?: "board" | "settings";
}) {
  const { logout } = useAuth();

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      spacing={2}
      useFlexGap
      sx={{
        justifyContent: "space-between",
        alignItems: { xs: "stretch", lg: "center" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.25,
          minHeight: 28,
        }}
      >
        {leading}
        <Typography
          component="span"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "text.secondary",
            fontSize: "1rem",
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>
        {trailing}
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        useFlexGap
        sx={{
          alignItems: "center",
          alignSelf: { xs: "flex-start", lg: "flex-end" },
          flexWrap: "wrap",
          rowGap: 1,
        }}
      >
        {active === "board" ? (
          <HeaderIconTooltip title="Settings">
            <IconButton
              component={Link}
              to="/configuration"
              aria-label="Open settings"
              size="small"
              sx={iconButtonSx}
            >
              <SettingsOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </HeaderIconTooltip>
        ) : (
          <HeaderIconTooltip title="Board">
            <IconButton
              component={Link}
              to="/dashboard"
              aria-label="Open board"
              size="small"
              sx={iconButtonSx}
            >
              <DashboardOutlinedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </HeaderIconTooltip>
        )}
        {actions}
        <HeaderIconTooltip title="Sign out">
          <IconButton
            aria-label="Sign out"
            onClick={logout}
            size="small"
            sx={iconButtonSx}
          >
            <LogoutOutlinedIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </HeaderIconTooltip>
      </Stack>
    </Stack>
  );
}

export { HeaderIconTooltip };
